import { Injectable, OnDestroy } from '@angular/core';
import { BehaviorSubject, Observable, Subject } from 'rxjs';
import { filter, map, takeUntil } from 'rxjs/operators';
import { SelectItem } from '@mydocma/dynamic-form/lib/fields/filter-select/select-item';
import { MmViewService } from '../../mm-view.service';
import { FieldsFilterDataService } from './fields-filter-data.service';
import { Logger } from '@mydocma/common/src/lib/logger';

@Injectable()
export class FieldsUserFilterService implements OnDestroy {

  private currentProjectId: number;
  private projectUsersSubject = new BehaviorSubject<SelectItem[]>([]);
  private filteredCreatorsSubject = new BehaviorSubject<SelectItem[]>([]);
  private filteredModifiersSubject = new BehaviorSubject<SelectItem[]>([]);
  
  public projectUsers$ = this.projectUsersSubject.asObservable();
  public filteredCreators$ = this.filteredCreatorsSubject.asObservable();
  public filteredModifiers$ = this.filteredModifiersSubject.asObservable();
  
  private destroy$ = new Subject<void>();
  
  constructor(
    private readonly mmViewService: MmViewService,
    private readonly dataService: FieldsFilterDataService,
    private readonly logger: Logger
  ) {
    this.initialize();
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  private initialize(): void {
    this.mmViewService.currentProject$
      .pipe(
        filter(project => !!project),
        takeUntil(this.destroy$)
      )
      .subscribe(project => {
        this.currentProjectId = project.Id;
        this.loadProjectUsers();
      });
      
    this.dataService.userCache$
      .pipe(
        filter(cache => !!cache && cache.projectId === this.currentProjectId),
        map(cache => cache.users),
        takeUntil(this.destroy$)
      )
      .subscribe(users => {
        this.projectUsersSubject.next(users);

        this.filteredCreatorsSubject.next(users);
        this.filteredModifiersSubject.next(users);
      });
  }

  private async loadProjectUsers(): Promise<void> {
    if (!this.currentProjectId) {
      console.log('Not loading users - missing project ID');
      return;
    }

    try {
      const users = await this.dataService.loadProjectUsers(this.currentProjectId);
      this.projectUsersSubject.next(users);
      this.filteredCreatorsSubject.next(users);
      this.filteredModifiersSubject.next(users);
    } catch (error) {
      this.logger.error(error, '', 'FieldsUserFilterService');
      this.projectUsersSubject.next([]);
      this.filteredCreatorsSubject.next([]);
      this.filteredModifiersSubject.next([]);
    }
  }

  public async forceLoadUsers(): Promise<SelectItem[]> {
    if (this.currentProjectId) {
      const users = await this.dataService.loadProjectUsers(this.currentProjectId, true);
      this.projectUsersSubject.next(users);
      this.filteredCreatorsSubject.next(users);
      this.filteredModifiersSubject.next(users);
      return users;
    }
    return [];
  }


  public filterUsersBySearchResults(creatorIds: number[], modifierIds: number[]): void {
    const allUsers = this.projectUsersSubject.value;
    if (!allUsers.length) return;
    const meUser = allUsers.find(user => (user as any)?.data?.isMe === true) || allUsers.find(user => user.text.includes('('));
    
    const creatorIdStrings = creatorIds.map(id => id.toString());
    let filteredCreators = allUsers.filter(user => 
      creatorIdStrings.includes(user.id) || user === meUser
    );
    
    const modifierIdStrings = modifierIds.map(id => id.toString());
    let filteredModifiers = allUsers.filter(user => 
      modifierIdStrings.includes(user.id) || user === meUser
    );
    
    if (meUser) {
      filteredCreators = [meUser, ...filteredCreators.filter(user => user !== meUser)];
      filteredModifiers = [meUser, ...filteredModifiers.filter(user => user !== meUser)];
    }
    
    if (filteredCreators.length === 0 && meUser) {
      filteredCreators = [meUser];
    }
    if (filteredModifiers.length === 0 && meUser) {
      filteredModifiers = [meUser];
    }
    
    this.filteredCreatorsSubject.next(filteredCreators);
    this.filteredModifiersSubject.next(filteredModifiers);
  }

  public resetUserFilters(): void {
    const allUsers = this.projectUsersSubject.value;
    this.filteredCreatorsSubject.next(allUsers);
    this.filteredModifiersSubject.next(allUsers);
  }

  public showOnlyMeUser(): void {
    const allUsers = this.projectUsersSubject.value;
    const meUser = allUsers.find(user => (user as any)?.data?.isMe === true) || allUsers.find(user => user.text.includes('('));
    
    const meUserOnly = meUser ? [meUser] : [];
    this.filteredCreatorsSubject.next(meUserOnly);
    this.filteredModifiersSubject.next(meUserOnly);
  }

  public getUserDisplayNameById(userId: string): Observable<string> {
    if (!this.currentProjectId) {
      throw new Error('[getUserDisplayNameById] No project ID found, cannot get display name for user: ' + userId);
    }
    return this.dataService.getUserDisplayNameById(userId, this.currentProjectId);
  }
}